package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.PointerEvents
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.pointerEvents
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.right
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text
import kotlin.random.Random

data class LSelectOption(
    val value: String,
    val label: String,
    val disabled: Boolean = false,
)

val LSelectStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .height(ControlHeight.MD)
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .color(colorMode.toSitePalette().textPrimary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Control)
            .padding(left = Space.MD, right = Space.Huge)
            .fontSize(TypeScale.BodySmall)
            .styleModifier {
                property("appearance", "none")
            }
            .transitionColors()
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
    // No `[aria-invalid="true"]` paint — hard rule 4, status colour never touches an interactive
    // control. `aria-invalid` still ships, so assistive technology is told; the error sentence
    // beneath the select is what a sighted reader is told. See LTextFieldFrameStyle for the
    // full reasoning and the spec lines it comes from.
}

val LSelectIconStyle = CssStyle.base {
    Modifier
        .position(Position.Absolute)
        .right(Space.MD)
        .color(colorMode.toSitePalette().textMuted)
        .pointerEvents(PointerEvents.None)
}

@Composable
fun LSelect(
    value: String,
    options: List<LSelectOption>,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    /**
     * Names the control when no visible [label] is right.
     *
     * A select inside a table row is the case: a visible "Role" label on every one of forty rows is
     * noise a sighted reader does not need, because the column header and the row already say it.
     * Without this the control reaches a screen reader with no name at all and is announced as its
     * current value — forty combo boxes called "Admin" and "Translator", none of which says whose.
     */
    accessibleLabel: String? = null,
    error: String? = null,
    enabled: Boolean = true,
) {
    val inputId = remember { "lingvo-select-${Random.nextInt(1_000_000)}" }
    val errorId = "$inputId-error"
    com.varabyte.kobweb.compose.foundation.layout.Column(
        LFieldGroupStyle.toModifier()
            .then(modifier)
            .attr("data-disabled", (!enabled).toString()),
    ) {
        if (label != null) {
            Label(forId = inputId, attrs = LFieldLabelStyle.toModifier().toAttrs()) {
                Text(label)
            }
        }
        // The chevron is absolutely positioned, so this wrapper must establish the containing
        // block. Without it the arrow escapes to the nearest positioned ancestor (the page).
        //
        // **`width: 100%`, or the component silently ignores the width it was given.** The caller's
        // [modifier] lands on the Column above; this Box is a flex child of that Column, whose
        // cross-axis alignment is `flex-start`, so without a width of its own it shrink-wrapped —
        // and `LSelectStyle`'s own `width: 100%` then resolved against the select's *intrinsic*
        // width rather than the Column's. Four filter selects asked for the same width and drew
        // four different ones, each sized to its own longest option. Percentages resolve to `auto`
        // during intrinsic sizing, so a Column that shrink-wraps is measured exactly as before.
        Box(
            Modifier.position(Position.Relative).width(100.percent),
            contentAlignment = Alignment.CenterEnd,
        ) {
            Select(
                attrs = LSelectStyle.toModifier()
                    .toAttrs {
                        id(inputId)
                        if (label == null && accessibleLabel != null) attr("aria-label", accessibleLabel)
                        if (!enabled) disabled()
                        if (error != null) {
                            attr("aria-invalid", "true")
                            attr("aria-describedby", errorId)
                        }
                        onChange { selectedValue ->
                            selectedValue.value?.let(onValueChange)
                        }
                    },
            ) {
                options.forEach { option ->
                    Option(
                        value = option.value,
                        attrs = {
                            if (option.value == value) selected()
                            if (option.disabled) disabled()
                        },
                    ) {
                        Text(option.label)
                    }
                }
            }
            Box(LSelectIconStyle.toModifier()) {
                LChevronDownIcon()
            }
        }
        if (error != null) {
            org.jetbrains.compose.web.dom.Div(LFieldErrorStyle.toModifier().toAttrs {
                id(errorId)
                attr("role", "alert")
            }) {
                Text(error)
            }
        }
    }
}
